import { Component, OnInit } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { PAGE_PERFORMANCE_AUDIT_RELATIONS } from '../../constants/performance-audit-relations';
import { PagePerformanceAuditInfoService } from '../../services/page-performance-audit-info/page-performance-audit-info.service';
import { PagePerformancePageDetailsService } from '../../services/page-performance-page-details/page-performance-page-details.service';
import { AuditImpactService } from '../../services/audit-impact/audit-impact.service';
import { PagePerformanceDiagnosticsService } from './service/diagnostics.service';
import { orderBy, safeClone } from '@client/app/shared/utils/object-utils';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { ExporterService } from '@client/app/modules/export/services/exporter.service';
import {
    PagePerformanceDialogsAuditDetailsComponent,
} from '@client/app/modules/page-performance/dialogs/audit-details/audit-details.component';

@Component({
    selector: 'page-performance-page-details-diagnostics',
    standalone: false,
    template: `
        <div class="page-performance-page-details-performance-diagnostics">
            <div class="d-flex align-items-center mb-0 mt-3">
                <span class="fa-2x fa-fw mr-2 text-link"
                      aria-hidden="true"
                      [class]="(['PAGE_PERFORMANCE', 'PERFORMANCE', 'DIAGNOSTICS'] | monIcons)">
                </span>
                <div class="d-flex align-items-center">
                    <h2 class="font-weight-300 my-1" translate>
                        Diagnostics
                    </h2>
                </div>
            </div>
            <p class="text-gray-small ml-8 mb-4" translate>
                Diagnostics, which flags particular problems that currently exist on your site that need to be addressed,
                like having file sizes that are too large that are potentially slowing down loading times. They'll let you know
                exactly what the problem is, how it's impacting your site, and why it needs to be fixed.
            </p>
            <mon-tabs [(status)]="tab"
                      [options]="tabs"
                      (onStatus)="setTab($event)"
                      (onSearch)="onSearch($event)"
                      [showSearch]="true"
                      [search]="search"
                      [showFiltersBtn]="true"
                      [(isFiltersOpen)]="isFiltersOpen"
                      [minSearchLength]="minSearchCharacterLimit">
                <button type="button"
                        class="btn btn-border-radius btn-secondary export-btn-height mr-1 font-size-1-1"
                        ngCsv
                        [data]="exportCsv()"
                        [quoteStrings]="true"
                        fieldSep=";"
                        [filename]="csvFileName()"
                        [attr.aria-label]="'Export CSV' | translate">
                    <span class="fas fa-download fa-lg pr-1" aria-hidden="true"></span>
                    <span translate>Export</span>
                </button>
            </mon-tabs>
            <div class="card panel-monsido-card">
                <div class="card-body">
                    <div *ngIf="isFiltersOpen" class="merge-t-8 merge-r-16 merge-l-16 mb-3">
                        <table-filters [(open)]="isFiltersOpen"
                                       [(ngModel)]="filters"
                                       [filters]="availableFilters"
                                       (onChanges)="onFiltersChange($event)">
                        </table-filters>
                    </div>
                    <table class="table table-fixed table-hover table-vertial-align" *ngIf="audits.length > 0">
                        <thead>
                            <tr>
                                <th class="col-sm-30"
                                    monTableSort
                                    monSortBy="title"
                                    [monSortDirection]="sortDirection"
                                    [monCurrentlySortedBy]="sortType"
                                    (monOnSorted)="onSortPage($event)">
                                    {{ 'Audit' | translate }}
                                </th>
                                <th class="col-sm-6 text-right pr-7"
                                    monTableSort
                                    monSortBy="difficulty"
                                    [monSortDirection]="sortDirection"
                                    [monCurrentlySortedBy]="sortType"
                                    (monOnSorted)="onSortPage($event)">
                                    <span translate>Difficulty</span>
                                    <span class="fas fa-question-circle"
                                          aria-hidden="true"
                                          [ngbTooltip]="'Indicates the difficulty to fix an audit error' | translate">
                                    </span>
                                </th>
                                <th class="col-sm-6"
                                    monTableSort
                                    monSortBy="priority"
                                    [monSortDirection]="sortDirection"
                                    [monCurrentlySortedBy]="sortType"
                                    (monOnSorted)="onSortPage($event)">
                                    {{ 'Priority' | translate }}
                                </th>
                                <th class="col-sm-4 text-right"
                                    translate>
                                    Relevant for
                                </th>
                                <th class="col-sm-6">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                        <tr *ngFor="let audit of audits; trackBy: trackByAuditId">
                            <td>
                                <div class="text-ellipsis text-bold">
                                    <span class="inline-block">{{audit.title}}</span>&nbsp;
                                    <span class="inline-block" *ngIf="audit.displayValue">
                                        - {{audit.displayValue}}
                                    </span>
                                </div>
                                <mon-markdown-viewer class="markdown-container" *ngIf="audit.description" [monMarkdown]="audit.description"></mon-markdown-viewer>
                            </td>
                            <td class="text-right pr-7">
                                {{ getDifficulty(audit.id) }}
                            </td>
                            <td>
                                <span [ngClass]="getLabelClass(audit)">
                                    {{getImpactTranslation(audit)}}
                                </span>
                            </td>
                            <td class="text-right">
                                {{audit.metrics.join(', ')}}
                            </td>
                            <td class="text-right">
                                <button type="button" (click)="openDetails(audit)" class="btn btn-border-radius btn-secondary" translate>
                                    Details
                                </button>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    <div *ngIf="audits.length === 0">
                        <div class="font-weight-300 text-grey text-center text-size-h3" translate>No diagnostics issues were found</div>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class PagePerformancePageDetailsDiagnosticsComponent implements OnInit {
    tab: string = 'errors';
    search: string = '';
    minSearchCharacterLimit: number = 3;
    sortType: string = 'title';
    sortDirection: any = 'desc';
    isFiltersOpen: boolean = false;
    audits: any[] = [];
    allAudits: any[] = [];
    tabs: any[] = [];
    filters: any = {};
    availableFilters: any = {};
    noResults: boolean = false;

    constructor (
        private pagePerformanceAuditInfoService: PagePerformanceAuditInfoService,
        private pagePerformancePageDetailsService: PagePerformancePageDetailsService,
        private auditImpactService: AuditImpactService,
        private pagePerformanceDiagnosticsService: PagePerformanceDiagnosticsService,
        private translateService: TranslateService,
        private monEventService: MonEventService,
        private exporterService: ExporterService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    getLoadingStatus (): boolean {
        return this.pagePerformancePageDetailsService.getLoadingReportStatus();
    }

    getLabelClass (audit: any): string {
        return this.auditImpactService.getImpactClass(audit);
    }

    getImpactTranslation (audit: any): string {
        return this.auditImpactService.getImpactTranslation(audit);
    }

    setTab (tabValue: any): void {
        this.tab = tabValue;
        this.getPage();
    }

    onSearch (search: any): void {
        this.search = search;
        this.getPage(true);
    }

    openDetails (audit: any): void {
        const params = {
            body: PagePerformanceDialogsAuditDetailsComponent,
            size: 'sm',
            data: {
                audit: audit,
            },
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    getDifficulty (id: string): string {
        return this.pagePerformanceAuditInfoService.getDifficulty(id);
    }

    onSortPage (event: any): void {
        this.sortDirection = event.direction;
        this.sortType = event.by;
        this.getPage(true);
    }

    exportCsv (): any {
        return () => {
            if (this.audits) {
                return this.exporterService.parsePerformanceAudits(this.audits);
            } else {
                return null;
            }
        };
    }

    csvFileName (): string {
        if (this.tab === 'errors') {
            return this.translateService.getString('diagnostics_errors.csv');
        }
        return this.translateService.getString('diagnostics_passed.csv');
    }

    onFiltersChange (changes: any): void {
        if (!changes.audits.length) {
            this.initFilters();
        }

        if (changes.audits) {
            this.getPage();
        }
    }

    trackByAuditId (index: number, audit: any): any {
        return audit.id;
    }

    // PROTECTED
    private activate (): void {
        this.initFilters();
        this.tab = 'errors';
        this.search = '';
        this.minSearchCharacterLimit = 3;
        this.sortType = 'title';
        this.sortDirection = 'desc';
        this.isFiltersOpen = false;
        this.audits = [];
        this.allAudits = this.pagePerformanceDiagnosticsService.getAudits().map((entry: any) => {
            const updatedEntry = safeClone(entry);
            updatedEntry.metrics = PAGE_PERFORMANCE_AUDIT_RELATIONS.metrics[entry.id] || [];
            return updatedEntry;
        });
        this.setupTabs();
        this.getPage();
    }

    private initFilters (): void {
        const filters = this.pagePerformancePageDetailsService.initFilters();
        this.filters = filters.filters;
        this.availableFilters = filters.availableFilters;
    }

    private getPage (forceDomUpdate?: boolean): void {
        const filteredAudits = this.pagePerformancePageDetailsService.filterAudits(this.filters, this.allAudits, this.tab, this.search);
        this.audits = this.sortAudits(filteredAudits);
        // Renews the Tab Count when filtering search results.
        this.setupTabs();
    }

    private sortAudits (audits: any[]): any[] {
        return orderBy(
            audits,
            [
                (audit: any) => {
                    if (this.sortType !== null) {
                        if (this.sortType === 'difficulty') {
                            return this.pagePerformanceAuditInfoService.getDifficultyLevel(audit.id);
                        } else if (this.sortType === 'priority') {
                            return this.auditImpactService.getImpactValue(audit);
                        } else {
                            return audit[this.sortType];
                        }
                    }
                    return null;
                },
            ],
            [this.sortDirection as any],
        );
    }

    private setupTabs (): void {
        let errorAudits: number;
        let passedAudits: number;
        if (this.tab === 'errors') {
            errorAudits = this.audits.length;
            passedAudits = this.allAudits.length - errorAudits;
        } else {
            passedAudits = this.audits.length;
            errorAudits = this.allAudits.length - passedAudits;
        }
        this.tabs = [
            {
                name: this.translateService.getString('Errors ([[audits]])', { audits: errorAudits }),
                value: 'errors',
            },
            {
                name: this.translateService.getString('Passed ([[audits]])', { audits: passedAudits }),
                value: 'passed',
            },
        ];
    }
}
